import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import type { User } from '@shared/schemas';
import { ThemeSwitcher } from './ThemeSwitcher';
// slot_pomodoro_rework PR.5 (17 MAY 2026): Pomodoro pill mount removed.
// Pomodoro UX moved к viewport-level floating FAB widget (PomodoroWidget.tsx
// mounted в App.tsx Router · per Genie picks Q1+Q4 17 MAY · single bottom-right
// positioning paradigm для desktop + mobile). `showTimer` prop preserved as
// deprecated no-op для caller backward compat (17 callsites pass showTimer={false}).
// Old Pomodoro.tsx retired к stub в same PR · single source of truth = FAB widget.
// slot_vocabulary_navbar (17 MAY 2026): GlossaryButton import dropped.
// Glossary entry promoted to Sidebar foot (single source of truth · Q3=a).
// Component preserved at src/components/GlossaryButton.tsx for the in-context
// CockpitOverview shortcut on Sprint screens (Phase 2 stretch).
import { DevToggle } from './DevToggle';
import { TextSizeControl } from './TextSizeControl';
import { roleOf, isDevToggleVisible } from '@/lib/access';
// H.4-EXT.2 (17 MAY 2026 slot_riso_phase_h_final_smoke): DensityToggle +
// DecorationToggle unmounted from production topbar per Genie 17 MAY visual
// review (Riso Final design has no UI-prefs cluster in topbar). A1 migration
// miss surfaced by visual smoke. Components preserved in codebase
// (src/components/DensityToggle.tsx + DecorationToggle.tsx) for future
// TweaksPanel integration or alternate surface. Phase 3.7 M8 ASK-2b-8=B
// decision superseded by RISO_FINAL_DESIGN umbrella ship state.

export interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
  here?: boolean;
}

// slot_ux_polish UXP.2 (2026-05-23): TopbarNavContext.
// The avatar lives in Topbar but the things it needs — навигация на «Профиль»,
// `logout`, и `user` для инициалов — живут выше (App.tsx Router, который смонтирован
// внутри <AuthProvider>). Topbar НЕ зовёт useAuth() напрямую: useAuthContext()
// кидает вне провайдера, а ~12 screen-тестов рендерят Topbar без него. Вместо этого
// Router кормит этот лёгкий контекст, а Topbar читает его через useContext (вне
// провайдера → null → graceful fallback). Это форма с наименьшим числом тронутых
// файлов (Topbar + App + CSS · 0 screen-тестов).
export interface TopbarNavValue {
  /** Navigate to the «Профиль» screen. */
  goProfile: () => void;
  /** Navigate to the admin «Управление ролями» screen (admin-only · optional). */
  goAdmin?: () => void;
  /** Sign out · clears the Supabase session → AuthGate → Login. */
  logout: () => void | Promise<void>;
  /** Signed-in user · drives avatar initials + menu header. */
  user: User | null;
}

const TopbarNavContext = createContext<TopbarNavValue | null>(null);

export function TopbarNavProvider({
  value,
  children,
}: {
  value: TopbarNavValue;
  children: ReactNode;
}) {
  return <TopbarNavContext.Provider value={value}>{children}</TopbarNavContext.Provider>;
}

function topbarInitials(user: User | null): string {
  const name = (user?.displayName ?? '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    const first = parts[0] ?? '';
    const second = parts[1] ?? '';
    const letters = second ? first.charAt(0) + second.charAt(0) : first.slice(0, 2);
    return letters.toUpperCase();
  }
  const email = (user?.email ?? '').trim();
  if (email) return email.slice(0, 2).toUpperCase();
  return '?';
}

interface TopbarProps {
  crumb?: BreadcrumbItem[];
  onLogo?: () => void;
  /**
   * @deprecated · slot_pomodoro_rework PR.5 (17 MAY 2026) · ignored.
   * Pomodoro moved к viewport-level PomodoroWidget FAB (App.tsx mount · z=42).
   * Prop preserved для backward compat across 17 callsites. Future cleanup
   * pass can drop prop entirely + remove showTimer={false} call sites.
   */
  showTimer?: boolean;
  rightExtra?: ReactNode;
  /**
   * slot_vocabulary_navbar (17 MAY 2026): kept in the prop API for backward
   * compatibility — Sprint.tsx and other callers may still pass it. Currently
   * unused inside Topbar (GlossaryButton render dropped per Q3=a · single
   * source of truth = Sidebar foot intercepted by NavShell). Future cleanup
   * can drop the prop entirely once all callers are migrated.
   */
  onOpenGlossary?: () => void;
}

/**
 * Avatar + account menu · slot_ux_polish UXP.2 (2026-05-23).
 *
 * The avatar is a button: click toggles a dropdown with «Профиль» (navigates)
 * and «Выйти» (signs out). Real initials come from the signed-in user. Outside
 * `<TopbarNavProvider>` (e.g. isolated screen tests) the context is null and
 * the menu degrades gracefully — initials fall back to «?», actions no-op.
 */
function AvatarMenu() {
  const nav = useContext(TopbarNavContext);
  const user = nav?.user ?? null;
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  // Point fix 2026-05-31 CF-PFB-2: portal-render dropdown к document.body so it
  // escapes .topbar stacking context (z-index 50). Position recomputed from
  // wrapRef.getBoundingClientRect() on open + on viewport resize/scroll.
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  const initials = topbarInitials(user);
  const label = user?.displayName?.trim() || user?.email || 'Аккаунт';
  const hasName = Boolean(user?.displayName?.trim());

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    const recompute = () => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 8,
        right: Math.max(8, window.innerWidth - rect.right),
      });
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // Click-outside: must miss BOTH wrapper (avatar button) AND popped
      // dropdown. Portal rendering moves dropdown outside wrapRef subtree.
      const insideWrap = wrapRef.current?.contains(target) ?? false;
      const insidePop = popRef.current?.contains(target) ?? false;
      if (!insideWrap && !insidePop) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleProfile = () => {
    setOpen(false);
    nav?.goProfile();
  };
  const handleAdmin = () => {
    setOpen(false);
    nav?.goAdmin?.();
  };
  // Admin-only «Управление ролями» entry · same gate as the DEV toggle.
  const showAdmin = isDevToggleVisible(roleOf(user)) && Boolean(nav?.goAdmin);
  const handleLogout = () => {
    setOpen(false);
    void nav?.logout();
  };

  const popover =
    open && pos && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={popRef}
            className="avatar-menu__pop"
            style={{ position: 'fixed', top: pos.top, right: pos.right }}
          >
            <div className="avatar-menu__head">
              <span className="avatar-menu__name">{label}</span>
              {hasName && user?.email && (
                <span className="avatar-menu__email">{user.email}</span>
              )}
            </div>
            <button type="button" className="avatar-menu__item" onClick={handleProfile}>
              Профиль
            </button>
            {showAdmin && (
              <button type="button" className="avatar-menu__item" onClick={handleAdmin}>
                Управление ролями
              </button>
            )}
            <button
              type="button"
              className="avatar-menu__item avatar-menu__item--logout"
              onClick={handleLogout}
            >
              Выйти
            </button>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="avatar-menu" ref={wrapRef}>
      <button
        type="button"
        className="avatar avatar--button"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Аккаунт: ${label}`}
        onClick={() => setOpen((v) => !v)}
      >
        {initials}
      </button>
      {popover}
    </div>
  );
}

export function Topbar({ crumb, onLogo, rightExtra }: TopbarProps) {
  // Topbar nav context (goProfile / logout / user) · null outside provider
  // (≈12 screen tests render Topbar without it → graceful fallbacks).
  const topbarNav = useContext(TopbarNavContext);
  const topbarUser = topbarNav?.user ?? null;

  const rightCluster = (
    <>
      {rightExtra}
      {/* slot_vocabulary_navbar (17 MAY 2026): GlossaryButton render dropped.
          Glossary now lives in Sidebar foot. See import-block docblock. */}
      {/* slot_pomodoro_rework PR.5 (17 MAY 2026): Pomodoro pill render dropped.
          Pomodoro moved к viewport-level FAB · `showTimer` prop now no-op. */}
      {/* H.4-EXT.2 (17 MAY): DensityToggle + DecorationToggle unmounted.
          See docblock at top of file. */}
      {/* MOBILE_READABILITY (6 JUN 2026): text-size A/A+/A++ cycle pill ·
          mirrored as full segment control on Profile. */}
      <TextSizeControl variant="cycle" />
      <ThemeSwitcher />
      {/* DEV unlock-all toggle is admin-only (slot_progression_plan_quiz Phase 2). */}
      {isDevToggleVisible(roleOf(topbarUser)) && <DevToggle />}
      {/* slot_ux_polish UXP.2 (23 MAY 2026): static <div.avatar>АК</div> replaced
          by <AvatarMenu/> — real initials + dropdown (Профиль / Выйти). */}
      <AvatarMenu />
    </>
  );

  return (
    <div className="topbar">
      <a
        className="logo"
        onClick={onLogo}
        role={onLogo ? 'button' : undefined}
        tabIndex={onLogo ? 0 : undefined}
        onKeyDown={
          onLogo
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onLogo();
                }
              }
            : undefined
        }
      >
        <span className="logo-dot">QA</span>
        <span>Studio</span>
      </a>
      {crumb && crumb.length > 0 && (
        <nav className="crumb" aria-label="Breadcrumb">
          {crumb.map((c, i) => (
            <span key={`${c.label}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {i > 0 && <span className="sep">/</span>}
              {c.onClick ? (
                <a
                  onClick={c.onClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      c.onClick?.();
                    }
                  }}
                >
                  {c.label}
                </a>
              ) : (
                <span className={c.here ? 'here' : ''} aria-current={c.here ? 'page' : undefined}>
                  {c.label}
                </span>
              )}
            </span>
          ))}
        </nav>
      )}
      {/* Wide-viewport cluster (≥1100px) */}
      <div className="right right--wide">{rightCluster}</div>
      {/* Narrow-viewport (<1100px): single avatar → Profile screen (logout +
          theme live there). Mobile nav cleanup (6 JUN 2026): replaces the
          vestigial «Меню» hamburger drawer whose portal avatar-dropdown rendered
          under the drawer overlay (logout/theme unreachable). Per Genie. */}
      <div className="right right--narrow">
        {/* MOBILE_READABILITY (6 JUN 2026): the tester's device IS the narrow
            viewport — the pill must be reachable without entering Profile. */}
        <TextSizeControl variant="cycle" />
        {/* slot_homework_rework Ф1 (17 JUN 2026 · Genie): theme toggle was only in
            Profile on narrow viewports (buried) — promote the compact ThemeSwitcher
            here next to text-size, same logic. Side rail isn't shown ≤1100. */}
        <ThemeSwitcher />
        <button
          type="button"
          className="avatar avatar--button"
          aria-label="Профиль и настройки"
          onClick={() => topbarNav?.goProfile()}
        >
          {topbarInitials(topbarUser)}
        </button>
      </div>
    </div>
  );
}
